/**
 * (auth)/_layout — persistent BrandPanel on desktop, plain Stack on mobile.
 *
 * The layout only hoists `<BrandPanel>` so its 5 entrance animations don't
 * replay on every route change. The right column is a bare `flex:1` canvas;
 * each screen's AuthScaffold owns its own ScrollView + column width + padding.
 *
 * On a phone — and on any portrait canvas, tablet included — the BrandPanel
 * is simply not rendered; see `useAuthBrandSplit`.
 *
 * The tree shape is IDENTICAL on both sides of the breakpoint on purpose.
 * Returning `<AuthStack />` from one branch and `<View>…</View>` from the
 * other made React unmount the whole Stack the moment a tablet rotated
 * across 900px, wiping every half-typed registration form. Only the
 * BrandPanel slot flips now, so the Stack — and the screen state inside
 * it — survives the resize.
 */
import { Platform, View } from 'react-native'
import { Stack } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { BrandPanel, useAuthBrandSplit } from '../../components/auth/BrandPanel'
import { LiquidCanvas, LiquidCanvasSceneBoundary } from '../../components/ui/LiquidBackdrop'
import { stableStackOptions } from '../../lib/navigation/stable-stack-options'

export const unstable_settings = {
  // Route through index first so Preview can select its read-only discovery
  // landing without changing the production Welcome default.
  initialRouteName: 'index',
}

// On web, Stack transitions re-mount screens — instant swaps keep the
// persistent chrome feel. Native keeps its natural push/fade.
const webSwap = Platform.OS === 'web' ? stableStackOptions : {}

function AuthStack() {
  return (
    <Stack
      initialRouteName="index"
      screenOptions={{ headerShown: false, ...stableStackOptions, ...webSwap }}
    >
      <Stack.Screen name="index" />
      <Stack.Screen name="welcome" />
      <Stack.Screen name="login" />
      <Stack.Screen name="register" />
      <Stack.Screen name="forgot-password" />
      <Stack.Screen name="phone-login" />
      <Stack.Screen name="phone-signup" />
      <Stack.Screen name="phone-verify" />
      <Stack.Screen name="verify-phone" />
      <Stack.Screen name="verify-email" />
      <Stack.Screen name="reset-password" />
    </Stack>
  )
}

export default function AuthLayout() {
  const { t } = useTranslation()
  // Wide AND landscape — see useAuthBrandSplit. A portrait tablet clears 900
  // but has no horizontal surplus to spend on a second column, so this slot
  // stays empty there and the Stack gets the whole canvas.
  const isSplit = useAuthBrandSplit()

  return (
    <View style={{ flex: 1, flexDirection: 'row' }}>
      {/* Persistent brand hero — mounts once for the whole auth segment.
          All 5 entrance animations inside fire on first mount and stay
          settled as the Stack navigates underneath. Below 900 this slot
          stays empty; the sibling canvas keeps its position either way. */}
      {isSplit ? (
        <LiquidCanvas isolate style={{ flex: 1 }}>
          <BrandPanel headline={t('welcome.headline')} subtitle={t('welcome.slogan')} />
        </LiquidCanvas>
      ) : null}

      {/* React context crosses a native Stack screen but native drawing does
          not. Reset ownership before the Stack so each auth route gets one
          real canvas rather than inheriting an invisible sibling plane. */}
      <LiquidCanvasSceneBoundary>
        <View style={{ flex: 1 }}>
          <AuthStack />
        </View>
      </LiquidCanvasSceneBoundary>
    </View>
  )
}
